"use client";
import { LocationCard as LocationCardUI } from "@repo/ui-parkmobile/LocationCard";
import type {
  LocationCard,
  LocationCardAmenitie,
} from "@/generated/sb";
import { storyblokEditable } from "@storyblok/react/rsc";
import {
  imageStoryblokResolver,
  assetResolver,
} from "@/lib/storyblok/adaptors";
import { SimpleRichText } from "./SimpleRichText";
import { ISbStoryData } from "storyblok-js-client";
import { trackCheckAvailabilityClick } from "@/utils/trackingScripts";

/**
 * Extended LocationCard type that includes migration-added fields
 */
interface LocationCardWithLocation extends LocationCard {
  locationId?: string;
}

interface LocationCardProps {
  blok: LocationCardWithLocation;
}

export default function LocationCard({ blok }: LocationCardProps) {
  if (!blok) {
    return null;
  }

  const locationImage = blok.locationImage?.[0]
    ? imageStoryblokResolver(blok.locationImage[0])
    : null;

  const amenities = blok.amenities
    ?.map((amenity) => {
      if (typeof amenity === "string") {
        return null;
      }

      const amenityStory =
        amenity as ISbStoryData<LocationCardAmenitie>;
      const amenityContent = amenityStory.content;

      if (!amenityContent) {
        return null;
      }

      const icon = amenityContent.icon
        ? assetResolver(amenityContent.icon)
        : null;
      return {
        title: amenityContent.title,
        icon: icon ? { src: icon.src, alt: icon.alt } : undefined,
      };
    })
    .filter((amenity) => amenity !== null);

  const description = blok.description?.[0] ? (
    <SimpleRichText
      blok={blok.description[0]}
      className="uipm:text-[var(--typography-colors-text-faded)] uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:leading-[var(--typography-line-heights-body-medium)]"
    />
  ) : undefined;

  const handleCheckAvailability = () => {
    trackCheckAvailabilityClick({
      section: blok.title || "Location Card",
      config: "locationCard",
      ctaText: "Check availability",
      parkingName: blok.title || "",
      location: blok.title || "",
      price: "",
      distance: "",
      address: blok.address || "",
      contentDescription: "",
    });
  };

  return (
    <LocationCardUI
      {...storyblokEditable(blok)}
      title={blok.title}
      address={blok.address}
      description={description}
      locationImage={locationImage}
      amenitiesTitle={blok.amenitiesTitle}
      amenities={amenities}
      topPadding={blok.topPadding}
      bottomPadding={blok.bottomPadding}
      datePlaceholder="Select date"
      dateFormat="MM/dd/yyyy"
      checkAvailabilityButtonText="Check availability"
      onCheckAvailability={handleCheckAvailability}
      locationId={blok.locationId}
    />
  );
}
